import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, ExternalLink, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { fetchMe, setToken, adminFetchArticle, adminCreateArticle, adminUpdateArticle, adminUploadImage, fileUrl, formatApiError } from "@/lib/auth";
import { fetchCategories } from "@/lib/api";
import { EditorRuler } from "@/pages/admin/EditorRuler";

const FALLBACK_CATEGORIES = [
    "Renda Fixa", "Ações e Dividendos", "Fundos Imobiliários",
    "Orçamento e Organização", "Independência Financeira", "Impostos e Planejamento",
];
const COVERS = [
    { value: "line", label: "Linha (tendência)" },
    { value: "candles", label: "Candlesticks" },
    { value: "bars", label: "Barras" },
    { value: "pulse", label: "Pulso" },
];

const EMPTY = {
    title: "", category: "", excerpt: "", author: "", author_role: "Equipe Investidor Comum",
    published_at: new Date().toISOString().slice(0, 10), read_minutes: 5, featured: false,
    cover_style: "line", cover_image: null, cover_aspect: "16:9",
    text_leading: "normal", text_spacing: "normal", text_padding: "sem",
    text_indent_left: 0, text_indent_right: 0,
    status: "published", content_html: "",
};

const ASPECT_CLASS = { "16:9": "aspect-video", "21:9": "aspect-[21/9]", "4:3": "aspect-[4/3]" };
const LEADING_VAR = { compacta: 1.6, normal: 1.85, ampla: 2.1 };
const SPACING_VAR = { compacto: "0.75rem", normal: "1.25rem", amplo: "2rem" };
const LEGACY_PAD_PX = { sem: 0, normal: 16, ampla: 40 };

const QUILL_MODULES = {
    toolbar: [
        [{ header: [2, 3, false] }, { size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["link", "clean"],
    ],
};

export default function ArticleEditor() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [form, setForm] = useState(EMPTY);
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const [slug, setSlug] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const uploadCover = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Imagem maior que 5 MB.");
            return;
        }
        setUploading(true);
        try {
            const res = await adminUploadImage(file);
            setForm((f) => ({ ...f, cover_image: res.path }));
            toast.success("Imagem de destaque enviada.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload.");
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        fetchCategories()
            .then((cats) => {
                const names = cats.map((c) => c.name);
                if (names.length) {
                    setCategories(names);
                    setForm((f) => (f.category ? f : { ...f, category: names[0] }));
                }
            })
            .catch(() => {});
        fetchMe().catch(() => {
            setToken(null);
            navigate("/admin/login", { replace: true });
        });
        if (isEdit) {
            adminFetchArticle(id)
                .then((a) => {
                    setForm({
                        title: a.title, category: a.category, excerpt: a.excerpt, author: a.author,
                        author_role: a.author_role || "Equipe Investidor Comum", published_at: a.published_at,
                        read_minutes: a.read_minutes, featured: a.featured, cover_style: a.cover_style,
                        cover_image: a.cover_image || null,
                        cover_aspect: a.cover_aspect || "16:9",
                        text_leading: a.text_leading || "normal",
                        text_spacing: a.text_spacing || "normal",
                        text_padding: a.text_padding || "sem",
                        text_indent_left: a.text_indent_left ?? (LEGACY_PAD_PX[a.text_padding] ?? 0),
                        text_indent_right: a.text_indent_right ?? (LEGACY_PAD_PX[a.text_padding] ?? 0),
                        status: a.status || "published",
                        content_html: a.content_html || "",
                    });
                    setSlug(a.slug);
                })
                .catch(() => toast.error("Artigo não encontrado."))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit, navigate]);

    const set = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm({ ...form, [k]: v });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, read_minutes: Number(form.read_minutes) || 5, content_text: "" };
            if (!payload.content_html || payload.content_html === "<p><br></p>") {
                toast.error("O corpo do artigo não pode estar vazio.");
                setSaving(false);
                return;
            }
            const res = isEdit ? await adminUpdateArticle(id, payload) : await adminCreateArticle(payload);
            setSlug(res.slug);
            toast.success(isEdit ? "Artigo atualizado." : "Artigo publicado.");
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full bg-ink border border-white/10 focus:border-gold/60 outline-none px-4 py-3 text-sm placeholder:text-slate-600 transition-colors";
    const labelCls = "block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2";

    if (loading) {
        return (
            <main className="min-h-screen flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-gold" />
            </main>
        );
    }

    return (
        <main data-testid="article-editor" className="pt-28 md:pt-32 pb-20 max-w-[800px] mx-auto px-4 md:px-6">
            <Link to="/admin" data-testid="editor-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-gold transition-colors mb-8">
                <ArrowLeft size={14} /> Voltar ao painel
            </Link>

            <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">
                {isEdit ? "Editar artigo" : "Novo artigo"}
            </h1>

            <form data-testid="article-editor-form" onSubmit={submit} className="flex flex-col gap-6">
                <div>
                    <label className={labelCls}>Título</label>
                    <input data-testid="editor-title-input" required minLength={5} value={form.title} onChange={set("title")} placeholder="Ex.: Como começar a investir com R$ 100" className={inputCls} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className={labelCls}>Categoria</label>
                        <select data-testid="editor-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Data de publicação</label>
                        <input data-testid="editor-date-input" type="date" required value={form.published_at} onChange={set("published_at")} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Minutos de leitura</label>
                        <input data-testid="editor-readtime-input" type="number" min={1} max={60} value={form.read_minutes} onChange={set("read_minutes")} className={inputCls} />
                    </div>
                </div>

                <div>
                    <label className={labelCls}>Resumo (excerpt)</label>
                    <textarea data-testid="editor-excerpt-input" required minLength={10} rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="Uma frase que resume o artigo e aparece nos cards." className={`${inputCls} resize-none`} />
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className={labelCls.replace(" mb-2", "") + " mb-0"}>Imagem de destaque</label>
                        <select
                            data-testid="editor-cover-aspect-select"
                            value={form.cover_aspect}
                            onChange={set("cover_aspect")}
                            className="bg-ink border border-white/10 focus:border-gold/60 outline-none px-3 py-1.5 text-xs transition-colors"
                        >
                            <option value="16:9">Proporção 16:9 (padrão)</option>
                            <option value="21:9">Proporção 21:9 (panorâmica)</option>
                            <option value="4:3">Proporção 4:3 (alta)</option>
                        </select>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadCover} className="hidden" data-testid="editor-cover-file-input" />
                    {form.cover_image ? (
                        <div className={`relative border border-white/10 overflow-hidden group ${ASPECT_CLASS[form.cover_aspect] || "aspect-video"}`}>
                            <img src={fileUrl(form.cover_image)} alt="Imagem de destaque" className="h-full w-full object-cover" />
                            <button
                                type="button"
                                data-testid="editor-cover-remove"
                                onClick={() => setForm({ ...form, cover_image: null })}
                                className="absolute top-3 right-3 p-2 bg-ink/80 border border-white/20 text-slate-300 hover:text-red-400 hover:border-red-400/60 transition-colors"
                                title="Remover imagem (volta para a capa generativa)"
                            >
                                <X size={15} />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            data-testid="editor-cover-upload-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className={`w-full border border-dashed border-white/20 hover:border-gold/60 text-slate-400 hover:text-gold transition-colors flex flex-col items-center justify-center gap-2 disabled:opacity-50 ${ASPECT_CLASS[form.cover_aspect] || "aspect-video"} max-h-48`}
                        >
                            {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                            <span className="text-xs uppercase tracking-[0.15em]">{uploading ? "Enviando..." : "Enviar imagem (JPG, PNG, WebP até 5 MB)"}</span>
                            <span className="text-[10px] text-slate-600">Sem imagem, a capa generativa é usada — a proporção se aplica aos dois casos</span>
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                        <label className={labelCls}>Autor</label>
                        <input data-testid="editor-author-input" required value={form.author} onChange={set("author")} placeholder="Nome do autor" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Cargo do autor</label>
                        <input data-testid="editor-author-role-input" value={form.author_role} onChange={set("author_role")} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Estilo da capa</label>
                        <select data-testid="editor-cover-select" value={form.cover_style} onChange={set("cover_style")} className={inputCls}>
                            {COVERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Status</label>
                        <select data-testid="editor-status-select" value={form.status} onChange={set("status")} className={inputCls}>
                            <option value="published">Publicado</option>
                            <option value="draft">Rascunho (não aparece no site)</option>
                        </select>
                    </div>
                </div>

                <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer select-none">
                    <input data-testid="editor-featured-checkbox" type="checkbox" checked={form.featured} onChange={set("featured")} className="w-4 h-4 accent-[#EAB308]" />
                    Exibir na seção de destaques da home
                </label>

                <div>
                    <label className={labelCls}>Legibilidade do texto</label>
                    <div className="border border-white/10 bg-paper p-4 flex flex-col gap-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Espaçamento de parágrafos</label>
                                <select data-testid="editor-spacing-select" value={form.text_spacing} onChange={set("text_spacing")} className={inputCls}>
                                    <option value="compacto">Compacto</option>
                                    <option value="normal">Normal</option>
                                    <option value="amplo">Amplo</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Distância entre linhas</label>
                                <select data-testid="editor-leading-select" value={form.text_leading} onChange={set("text_leading")} className={inputCls}>
                                    <option value="compacta">Compacta</option>
                                    <option value="normal">Normal</option>
                                    <option value="ampla">Ampla</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Régua de margens do texto</label>
                            <EditorRuler
                                left={form.text_indent_left}
                                right={form.text_indent_right}
                                onChange={([l, r]) => setForm((f) => ({ ...f, text_indent_left: l, text_indent_right: r }))}
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <label className={labelCls}>Corpo do artigo (texto rico)</label>
                    <div
                        className="editor-typo"
                        style={{
                            "--rh-leading": LEADING_VAR[form.text_leading] || 1.85,
                            "--rh-gap": SPACING_VAR[form.text_spacing] || "1.25rem",
                            "--rh-pad-l": `${form.text_indent_left}px`,
                            "--rh-pad-r": `${form.text_indent_right}px`,
                        }}
                    >
                        <ReactQuill
                            data-testid="editor-content-quill"
                            theme="snow"
                            value={form.content_html}
                            onChange={(html) => setForm({ ...form, content_html: html })}
                            modules={QUILL_MODULES}
                            placeholder="Escreva o artigo aqui... Use o cabeçalho para subtítulos, aspas para citações e listas para organizar."
                        />
                    </div>
                </div>

                <div className="flex items-center gap-4 pt-2">
                    <button
                        data-testid="editor-save-button"
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-gold text-ink font-medium px-8 py-3.5 text-sm hover:bg-golddark transition-colors disabled:opacity-60"
                    >
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                        {isEdit ? "Salvar alterações" : "Publicar artigo"}
                    </button>
                    {slug && (
                        <Link to={`/artigo/${slug}`} target="_blank" data-testid="editor-view-live" className="flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors">
                            <ExternalLink size={14} /> Ver publicado
                        </Link>
                    )}
                </div>
            </form>
        </main>
    );
}
